/**
 * The composer's text model, as blocks the editor can lay out.
 *
 * The update box used to be a transparent <textarea> over a coloured mirror div,
 * because a textarea cannot colour part of its text. Keeping those two layers
 * wrapping at the same width was a standing source of caret drift. This editor is
 * a single contenteditable instead: the caret lives in the very element that
 * draws the text, so there is nothing to keep in step. It also lets a list line
 * be its own block with a hanging indent, so a wrapped bullet tucks its second
 * line under the first letter of the item — the way a word processor does — while
 * you type, which a textarea can never do (its indent is one setting for the whole
 * box, not per line).
 *
 * This module is the PURE half: splitting the value into lines, recognising which
 * lines are list items and how deep their text hangs, and the arithmetic that maps
 * a plain-text caret offset to a (line, column) and back. The DOM half — walking
 * text nodes to place a real selection — lives in the editor component, thin, on
 * top of these. Pure so it can be unit-tested on Node, where there is no DOM.
 */

// The same two markers the renderer draws a list from (src/markdown.ts) and the
// auto-format continues (src/list-autoformat.ts). Kept here as their own copy so
// the editor's hanging indent lines up exactly with what those two produce.
const BULLET = /^(\s*)([-*])(\s+)/;
const NUMBER = /^(\s*)(\d+)([.)])(\s+)/;

export interface Line {
  /** The line's text, exactly as stored (markers and all — this is a plain-text
   *  editor, the characters stay visible and editable). */
  text: string;
  /** How many leading characters are the list marker (indent + bullet/number +
   *  the spaces after it), i.e. the column the wrapped lines hang to. 0 when the
   *  line is not a list item. */
  hang: number;
}

/** The marker width of a single line, or 0 when it is not a list item. */
export function hangWidth(line: string): number {
  const m = BULLET.exec(line) ?? NUMBER.exec(line);
  return m ? m[0].length : 0;
}

/** Split the value into lines, each tagged with its hang width. A value always
 *  has at least one line (the empty string is one empty line). */
export function toLines(value: string): Line[] {
  return value.split('\n').map((text) => ({ text, hang: hangWidth(text) }));
}

/** The length of each line's text — the shape the offset math runs on. */
export function lineLengths(value: string): number[] {
  return value.split('\n').map((l) => l.length);
}

export interface LineCol {
  line: number;
  col: number;
}

/**
 * A plain-text caret offset → the (line, column) it falls in.
 *
 * The value is the lines joined by '\n', so between line i and line i+1 sits one
 * newline character that belongs to no line. An offset at the very end of a line
 * maps to that line (col === its length); the next offset — one past the newline —
 * is the start of the following line (col 0). Those are two different offsets and
 * two different caret spots (end of a line vs start of the next), so the boundary
 * is resolved in favour of the EARLIER line, and only an offset strictly past the
 * newline moves on.
 */
export function offsetToLineCol(lens: number[], offset: number): LineCol {
  let o = Math.max(0, offset);
  for (let line = 0; line < lens.length; line++) {
    if (o <= lens[line]) return { line, col: o };
    o -= lens[line] + 1; // skip this line's text and the newline after it
  }
  // Past the end: clamp to the end of the last line.
  const last = lens.length - 1;
  return { line: last, col: lens[last] };
}

/** The inverse: a (line, column) → the plain-text offset. */
export function lineColToOffset(lens: number[], line: number, col: number): number {
  const l = Math.min(Math.max(0, line), lens.length - 1);
  let offset = 0;
  for (let i = 0; i < l; i++) offset += lens[i] + 1; // each earlier line + its newline
  return offset + Math.min(Math.max(0, col), lens[l]);
}

/** Do two values differ in their per-line LIST SHAPE (how many lines, and which
 *  are list items at what hang)? The editor leaves the browser's own DOM alone
 *  while this stays the same — plain typing within a line changes nothing here —
 *  and only rebuilds the blocks (restoring the caret) when it changes. */
export function listShapeChanged(a: string, b: string): boolean {
  const la = toLines(a);
  const lb = toLines(b);
  if (la.length !== lb.length) return true;
  for (let i = 0; i < la.length; i++) if (la[i].hang !== lb[i].hang) return true;
  return false;
}

/** One top-level node of the editor, reduced to what the value depends on: is it
 *  a line block, and what text does it hold. The DOM half maps the real child
 *  nodes onto this; the arithmetic stays here, pure and Node-testable. */
export interface CeNode {
  /** A line block (a top-level <div>) — its own line. A non-block is stray inline
   *  content the browser left at the root rather than inside a line. */
  isBlock: boolean;
  text: string;
}

/**
 * Reconstruct the plain-text value from the editor's top-level child nodes.
 *
 * In normal use every child is a line block and the value is their text joined by
 * newlines. But the browser can drift from that shape: Chrome and Edge on Windows,
 * laxer than WebKit, sometimes leave a typed character in a BARE TEXT NODE at the
 * root — a sibling of the line blocks, not inside one. Reading only the blocks
 * would miss it, so a just-typed word read back as "" and the composer's Post
 * button never lit. Folding stray top-level text into the line beside it (no line
 * break around it — only block-to-block gets a newline) means a typed character is
 * never dropped; the editor then rebuilds the canonical blocks on the next input.
 */
export function foldValue(nodes: CeNode[]): string {
  let out = '';
  let prevWasBlock = false;
  for (const n of nodes) {
    if (n.isBlock && prevWasBlock) out += '\n';
    out += n.text;
    prevWasBlock = n.isBlock;
  }
  return out;
}

// --- bold -----------------------------------------------------------------
//
// The composer shows bold as the reader will see it — visually heavy, not as the
// `**markers**` that carry it — so you compose what you get. But the editor's whole
// caret model (above) rests on ONE invariant: a line block's text, read back, is
// the value's text for that line, character for character. If the `**` were hidden
// by dropping them from the DOM, every offset would drift by two per marker.
//
// So the markers are never dropped: a bold run is drawn as the two `**`, kept as
// real text but painted zero-width (see .ce-bold-mark), wrapped around the inner
// text drawn heavy. The block's textContent is still exactly the value line —
// `**bold**` and all — and none of the arithmetic above has to change. segmentLine
// is the pure split the DOM builder draws from; it is this module's so the markers
// it keeps and the lengths it preserves can be unit-tested on Node.

// A bold run, mirroring the renderer's rule (src/markdown.ts): `**`, one or more
// characters that are neither `*` nor a line break, then `**`. Code spans are taken
// first and left whole — a `**` inside backticks is literal there too, exactly as
// the renderer treats it — so the composer bolds precisely what the feed will.
const BOLD_TOKEN = /(`[^`\n]+`)|(\*\*[^*\n]+\*\*)/g;

export type Seg =
  /** Ordinary text (drawn as-is, still @mention-tinted by the caller). */
  | { kind: 'plain'; text: string }
  /** A `**` marker — real text, painted zero-width so it takes no room. */
  | { kind: 'mark'; text: string }
  /** The text between a pair of markers — drawn heavy. */
  | { kind: 'bold'; text: string };

/** One line split into plain / marker / bold pieces. The pieces' texts joined
 *  back together are the line exactly — no character added or dropped — so the
 *  DOM built from them reads back as the same value. */
export function segmentLine(line: string): Seg[] {
  const out: Seg[] = [];
  let at = 0;
  for (const m of line.matchAll(BOLD_TOKEN)) {
    const start = m.index ?? 0;
    if (start > at) out.push({ kind: 'plain', text: line.slice(at, start) });
    const token = m[0];
    if (token.startsWith('`')) {
      // A code span — shown literally, not bolded, the way the renderer leaves it.
      out.push({ kind: 'plain', text: token });
    } else {
      out.push({ kind: 'mark', text: '**' });
      out.push({ kind: 'bold', text: token.slice(2, -2) });
      out.push({ kind: 'mark', text: '**' });
    }
    at = start + token.length;
  }
  if (at < line.length) out.push({ kind: 'plain', text: line.slice(at) });
  return out;
}

/** Does a line's bold SHAPE differ between two values — a stretch becoming bold or
 *  ceasing to be, a marker appearing or going? The editor leaves the browser's own
 *  edit in place while this (and the list shape) hold — typing inside a bold run
 *  just lengthens it, no rebuild — and rebuilds to draw the run heavy (or back to
 *  plain) only when the shape turns over, e.g. an empty `****` becomes `**x**`. The
 *  signature ignores the text's length, so ordinary typing within a run is no
 *  change; only the plain / marker / bold pattern matters. */
export function boldShapeChanged(a: string, b: string): boolean {
  const la = a.split('\n');
  const lb = b.split('\n');
  if (la.length !== lb.length) return true;
  const sig = (line: string) => segmentLine(line).map((s) => s.kind).join(',');
  for (let i = 0; i < la.length; i++) if (sig(la[i]) !== sig(lb[i])) return true;
  return false;
}

export interface BoldEdit {
  value: string;
  /** The selection to restore after the edit (collapsed when start === end). */
  start: number;
  end: number;
}

/** The width of a line's leading list marker + indent at a given point in the
 *  value, but only when that point is the START of its line — so wrapping a whole
 *  list item in bold keeps the "- " outside the markers and the line stays a list
 *  item. Elsewhere only leading whitespace is kept outside. */
function keepOutsideLeft(piece: string, atLineStart: boolean): number {
  if (atLineStart) {
    const h = hangWidth(piece);
    if (h > 0) return h;
  }
  return /^\s*/.exec(piece)![0].length;
}

/** Wrap one line's selected piece in `**…**`, keeping any leading list marker and
 *  the surrounding whitespace outside the markers. Returns the piece unchanged
 *  when there is nothing boldable in it (blank, or it already holds a `*`). */
function wrapPiece(piece: string, atLineStart: boolean): string {
  const lead = keepOutsideLeft(piece, atLineStart);
  const trail = piece.length - /\s*$/.exec(piece)!.index;
  const core = piece.slice(lead, piece.length - trail);
  if (core.trim() === '' || core.includes('*')) return piece;
  return piece.slice(0, lead) + '**' + core + '**' + piece.slice(piece.length - trail);
}

/**
 * Toggle bold over a selection, as ⌘B does — the one place the composer writes
 * `**` into the value, so the markers that reach storage are always well formed.
 *
 * Collapsed, it drops an empty pair with the caret between them, ready to type
 * into. Over a run already wrapped in `**` (whether the markers sit inside or
 * just outside the selection) it unwraps. Otherwise it wraps — line by line, so a
 * selection crossing a line break becomes valid bold on each line rather than one
 * `**…\n…**` the renderer would not read.
 */
export function toggleBold(value: string, start: number, end: number): BoldEdit {
  if (start > end) [start, end] = [end, start];

  if (start === end) {
    return { value: value.slice(0, start) + '****' + value.slice(start), start: start + 2, end: start + 2 };
  }

  const sel = value.slice(start, end);

  // Markers just outside the selection: **[sel]** → unwrap, selection follows in.
  if (value.slice(start - 2, start) === '**' && value.slice(end, end + 2) === '**') {
    return { value: value.slice(0, start - 2) + sel + value.slice(end + 2), start: start - 2, end: end - 2 };
  }
  // The selection itself is a bold run: [**sel**] → unwrap to its inner text.
  if (sel.startsWith('**') && sel.endsWith('**') && sel.length >= 5 && !sel.slice(2, -2).includes('*')) {
    const inner = sel.slice(2, -2);
    return { value: value.slice(0, start) + inner + value.slice(end), start, end: start + inner.length };
  }

  // Wrap, one line at a time.
  const atStart = start === 0 || value[start - 1] === '\n';
  const wrapped = sel
    .split('\n')
    .map((piece, i) => wrapPiece(piece, i === 0 ? atStart : true))
    .join('\n');
  return { value: value.slice(0, start) + wrapped + value.slice(end), start, end: start + wrapped.length };
}
